import { createContext, useContext, useMemo, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("dndi_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("dndi_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async ({ email, password, mode }) => {
    const { data } = await api.post("/auth/login", { email, password, mode });
    return data; // { challenge_token, mfa_setup, ... }
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data;
  };

  const resetMfa = async ({ email, password }) => {
    const { data } = await api.post("/auth/reset-mfa", { email, password });
    return data;
  };

  const mfaSetup = async (challenge_token) => {
    const { data } = await api.post("/auth/mfa/setup", { challenge_token });
    return data;
  };

  const mfaVerify = async ({ challenge_token, code }) => {
    const { data } = await api.post("/auth/mfa/verify", { challenge_token, code });
    if (data.access_token) localStorage.setItem("dndi_token", data.access_token);
    setUser({ ...data.user, mode: data.mode });
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("dndi_token");
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, resetMfa, mfaSetup, mfaVerify, logout, setUser }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
